import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { cachedGet } from 'utils/apiCache';
import { useDiagnostics } from 'context/DiagnosticsContext';

/**
 * Fetches a URL with TTL caching and returns { data, loading, error, timing }.
 * Re-fetches automatically when `url` changes (e.g. when timeRange changes).
 *
 * timing: { totalMs, fromCache, ageMs, serverMs, dbMs, cacheStatus }
 *
 * If `widgetId` is provided, timing is also reported to DiagnosticsContext
 * for the central perf panel.
 */
export function useWidgetFetch(url, { ttl, swr, enabled = true, widgetId = null } = {}) {
  const [state, setState] = useState({ data: null, loading: true, error: null, timing: null });
  const ctx = useDiagnostics();
  const reportTiming = ctx?.reportTiming;
  // Stable ref so the effect closure always has the latest reportTiming without
  // triggering re-renders when the context value object identity changes.
  const reportRef = useRef(reportTiming);
  reportRef.current = reportTiming;

  useEffect(() => {
    if (!enabled || !url) {
      setState(s => ({ ...s, loading: false }));
      return;
    }

    let cancelled = false;
    setState(s => ({ ...s, loading: true, error: null }));

    const t0 = performance.now();
    const meta = {};

    cachedGet(axios, url, { ttl, swr, meta })
      .then(data => {
        if (cancelled) return;
        const timing = {
          totalMs:     Math.round(performance.now() - t0),
          fromCache:   meta.fromCache    ?? false,
          ageMs:       meta.ageMs        ?? null,
          serverMs:    meta.serverMs     ?? null,
          dbMs:        meta.dbMs         ?? null,
          cacheStatus: meta.cacheStatus  ?? null,
        };
        setState({ data, loading: false, error: null, timing });
        if (widgetId && reportRef.current) {
          reportRef.current(widgetId, { ...timing, url });
        }
      })
      .catch(error => {
        if (cancelled) return;
        const timing = {
          totalMs:     Math.round(performance.now() - t0),
          fromCache:   false,
          ageMs:       null,
          serverMs:    null,
          dbMs:        null,
          cacheStatus: null,
        };
        setState({ data: null, loading: false, error, timing });
        if (widgetId && reportRef.current) {
          reportRef.current(widgetId, { ...timing, url, error: error?.message });
        }
      });

    return () => { cancelled = true; };
  }, [url, ttl, swr, enabled, widgetId]);

  return state;
}
